import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, X, ThumbsUp } from 'lucide-react';
import { api } from '../lib/api';

const DISMISS_KEY = 'jdv-top-of-month-dismissed';
const MONTH_NL = [
  'januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december'
];

export default function TopOfMonthFloat() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    const stored = localStorage.getItem(DISMISS_KEY);
    const currentMonthKey = new Date().toISOString().slice(0, 7);
    return stored === currentMonthKey;
  });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let active = true;
    api.get('/games/top-of-month')
      .then((resp) => {
        if (!active) return;
        setData(resp.data);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (loaded && data?.top && !dismissed) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [loaded, data, dismissed]);

  if (!loaded || dismissed || !data?.top) return null;

  const dismiss = () => {
    const currentMonthKey = new Date().toISOString().slice(0, 7);
    localStorage.setItem(DISMISS_KEY, currentMonthKey);
    setDismissed(true);
  };

  const monthLabel = MONTH_NL[new Date().getMonth()];
  const top = data.top;
  const art = top.box_art_url
    ? top.box_art_url.replace('{width}', '120').replace('{height}', '160')
    : null;

  return (
    <div
      className={`fixed left-4 z-[70] transition-all duration-500 ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}
      style={{ bottom: 'calc(1rem + var(--follower-ticker-h, 0px) + var(--stats-ticker-h, 0px))' }}
      data-testid="top-of-month-float"
    >
      <Link
        to="/vote"
        className="group flex items-stretch gap-3 max-w-[19rem] rounded-xl border border-violet-500/30 bg-zinc-950/90 backdrop-blur-md shadow-2xl shadow-violet-900/30 hover:border-violet-400/60 transition-colors overflow-hidden"
      >
        <div className="shrink-0 w-16 sm:w-20 bg-zinc-900 overflow-hidden relative">
          {art ? (
            <img
              src={art}
              alt={top.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-violet-600/30 to-fuchsia-600/20 flex items-center justify-center">
              <Flame className="w-7 h-7 text-violet-300" />
            </div>
          )}
        </div>

        <div className="flex-1 py-3 pr-9 min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-violet-300">
            <Flame className="w-3 h-3" />
            Top game-suggestie · {monthLabel}
          </div>
          <p className="text-white font-semibold leading-snug mt-1 line-clamp-2 group-hover:text-violet-200 transition-colors">
            {top.name}
          </p>
          <div className="flex items-center gap-1 mt-1.5 text-xs text-zinc-400">
            <ThumbsUp className="w-3 h-3 text-violet-300" />
            <span className="text-violet-200 font-semibold">
              {top.votes_this_month}
            </span>
            <span>
              {top.votes_this_month === 1 ? 'stem' : 'stemmen'} deze maand
            </span>
          </div>
        </div>
      </Link>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Sluiten"
        data-testid="top-of-month-dismiss"
        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 text-zinc-400 hover:text-white flex items-center justify-center transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
